import React, { useEffect, useRef } from "react";
import { useStore } from "../../../store/store";
import { useRequiredMsgs } from "../../../store/requiredMsgs";
import {
  group1SS,
  group1Sill,
  group2SS,
  group2Sill,
  group3SS,
  group3Sill,
  group4SS,
  group4Sill,
  group5SS,
  group5Sill,
  group6Sill,
  group7Sill,
  group8Sill,
} from "./defaults";

interface UpdateObj {
  sillFinish?: string;
  sillStyle?: string;
}

function useSillSelector() {
  const addToQuote = useStore((state) => state.addToQuote);

  const editBtn = useStore((state) => state.isEdit);

  const chosenConfig = useStore((state) => state.quote.exactConfig);
  const sliderPocket = useStore((state) => state.quote.sliderPocket);

  const sillStyleChoice = useStore((state) => state.quote.sillStyle);
  const sillColorChoice = useStore((state) => state.quote.sillColor);

  const paintPrimeChoice = useStore((state) => state.quote.paintAndPrime);
  const primeChoice = useStore((state) => state.quote.prime);
  const stainChoice = useStore((state) => state.quote.stain);
  const customPaintChoice = useStore((state) => state.quote.customPaintOrStain);

  const screenVal = useStore((state) => state.quote.addScreen);
  const screenType = useStore((state) => state.quote.screenType);
  const addSlidingScreen = useStore((state) => state.quote.addSlidingScreen);

  const slidingScrType = screenType && screenType === "Sliding Screen";
  const isCentorScr =
    screenVal && screenType?.includes("Centor") ? true : false;

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const withSlidingScreen =
    ["OX", "XO", "OA-XO", "OX-AO"].includes(chosenConfig) &&
    slidingScrType &&
    addSlidingScreen
      ? true
      : false;

  const showSillFinish =
    paintPrimeChoice || primeChoice || stainChoice || customPaintChoice
      ? true
      : false;

  const editRef = useRef(true);
  useEffect(() => {
    if (editBtn) return;
    editRef.current = true;
  }, [editBtn]);

  useEffect(() => {
    if (editBtn && editRef.current) {
      editRef.current = false;
    } else {
      if (!withSlidingScreen) {
        addToQuote({ sillStyle: "" });
      } else {
        addToQuote({ sillStyle: "Raised SILLution" });
      }
    }
  }, [withSlidingScreen]);

  const findSliderSillImg = () => {
    const nonpocket = "Sills/sliders/nonpocketing";
    const pocket = "Sills/sliders/pocketing";

    const scrLbl = isCentorScr ? "_CENTOR" : screenVal ? "_SCR" : "";

    if (group1Sill.includes(chosenConfig)) {
      const oxscrLbl = isCentorScr
        ? "_CENTOR"
        : withSlidingScreen && screenVal
        ? "_SLIDING_SCR"
        : screenVal
        ? "_SCR"
        : "";
      return `${nonpocket}/OX${oxscrLbl}`;
    } else if (group2Sill.includes(chosenConfig)) {
      return `${nonpocket}/OXX${scrLbl}`;
    } else if (group3Sill.includes(chosenConfig)) {
      return `${nonpocket}/OXXX${scrLbl}`;
    } else if (group4Sill.includes(chosenConfig)) {
      return `${nonpocket}/OXXXX${scrLbl}`;
    } else if (group5Sill.includes(chosenConfig)) {
      return `${pocket}/X${scrLbl}`;
    } else if (group6Sill.includes(chosenConfig)) {
      return `${pocket}/XX${scrLbl}`;
    } else if (group7Sill.includes(chosenConfig)) {
      return `${pocket}/XXX${scrLbl}`;
    } else if (group8Sill.includes(chosenConfig)) {
      return `${pocket}/XXXX${scrLbl}`;
    }
  };

  const findSliderSillSSImg = () => {
    const stainlessSteel = "Sills/sliders/StainlessSteel";

    const scrLbl = isCentorScr ? "_CENTOR" : screenVal ? "_SCR" : "";

    if (group1SS.includes(chosenConfig)) {
      return `${stainlessSteel}/X${scrLbl}`;
    } else if (group2SS.includes(chosenConfig)) {
      return `${stainlessSteel}/XX${scrLbl}`;
    } else if (group3SS.includes(chosenConfig)) {
      return `${stainlessSteel}/XXX${scrLbl}`;
    } else if (group4SS.includes(chosenConfig)) {
      return `${stainlessSteel}/XXXX${scrLbl}`;
    } else if (group5SS.includes(chosenConfig)) {
      return `${stainlessSteel}/XXXXX${scrLbl}`;
    }
  };

  const sillImageShown =
    !sillStyleChoice || !chosenConfig
      ? ""
      : withSlidingScreen
      ? "Sills/sliders/nonpocketing/OX_SLIDING_SCR"
      : sillStyleChoice === "Raised SILLution"
      ? findSliderSillImg()
      : findSliderSillSSImg();

  const handleClickSill = (label: string, disabled?: boolean) => {
    if (disabled) return;

    const checked = label === sillStyleChoice;

    if (checked) {
      addToQuote({ sillStyle: "" }, "sillStyle");
    } else {
      let update: UpdateObj = {};

      if (label.includes("Steel")) update["sillFinish"] = "";

      if (findRequired("Sill Type") || findRequired("Sill Finish"))
        removeRequired("Sill Type");
      update["sillStyle"] = label;
      addToQuote(update);
    }
  };

  const handleClickColor = (label: string) => {
    const checked = label === sillColorChoice;

    if (checked) {
      addToQuote({ sillColor: "" });
    } else {
      if (findRequired("Sill Type") || findRequired("Sill Finish"))
        removeRequired("Sill Finish");
      addToQuote({ sillColor: label });
    }
  };

  return {
    handleClickColor,
    handleClickSill,
    sillImageShown,
    showSillFinish,
    withSlidingScreen,
    sliderPocket,
    sillStyleChoice,
    sillColorChoice,
  };
}

export default useSillSelector;
